import { useCallback, useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { api } from '../../api.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { Logo } from '../../components/Logo.tsx';
import { formatEuros } from '../../format.ts';
import { PayoutSection } from './PayoutSection.tsx';

interface Me {
  referrer: { code: string; companyName: string | null; status: string };
  link: string;
  summary: { referrals_count: number; active_extras_count: number; held_cents: number; due_cents: number; invoiced_cents: number; paid_cents: number };
  minPayoutCents: number;
}

interface Referral {
  id: string;
  firstName: string;
  lastInitial: string;
  joinedAt: string;
  closedMissions: number;
  status: 'INSCRIT' | 'PROFIL_CREE' | 'ACTIF';
}

interface Commission {
  id: string;
  amount_cents: number;
  share_pct: string;
  created_at: string;
  eligible_at: string;
  paid_at: string | null;
  status: 'HELD' | 'DUE' | 'INVOICED' | 'PAID' | 'REVERSED';
  extra_first_name: string;
}

const REFERRAL_LABELS: Record<Referral['status'], { label: string; badge: string }> = {
  INSCRIT: { label: 'Inscrit', badge: 'badge-neutral' },
  PROFIL_CREE: { label: 'Profil créé', badge: 'badge-pending' },
  ACTIF: { label: 'Actif', badge: 'badge-available' },
};

const COMMISSION_LABELS: Record<Commission['status'], { label: string; badge: string }> = {
  HELD: { label: 'En carence', badge: 'badge-pending' },
  DUE: { label: 'Payable', badge: 'badge-available' },
  INVOICED: { label: 'Facturée', badge: 'badge-pending' },
  PAID: { label: 'Payée', badge: 'badge-neutral' },
  REVERSED: { label: 'Annulée', badge: 'badge-unavailable' },
};

export function ReferrerDashboard() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  const loadAll = useCallback(() => {
    api.get<Me>('/referrer/me').then(setMe);
    api.get<{ referrals: Referral[] }>('/referrer/referrals').then((res) => setReferrals(res.referrals));
    api.get<{ commissions: Commission[] }>('/referrer/commissions').then((res) => setCommissions(res.commissions));
  }, []);

  useEffect(loadAll, [loadAll]);

  function copy(kind: 'link' | 'code', value: string) {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  if (!me) return <div className="empty-state" style={{ paddingTop: 80 }}>Chargement…</div>;

  const kpis = [
    { label: 'Extras parrainés', value: String(me.summary.referrals_count) },
    { label: 'Extras actifs', value: String(me.summary.active_extras_count) },
    { label: 'En carence', value: formatEuros(me.summary.held_cents) },
    { label: 'Payable', value: formatEuros(me.summary.due_cents) },
    { label: 'Facturé, à verser', value: formatEuros(me.summary.invoiced_cents) },
    { label: 'Déjà versé', value: formatEuros(me.summary.paid_cents) },
  ];

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 20px 48px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Logo size={26} />
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Espace apporteur d'affaires · {user?.firstName} {user?.lastName}
          </div>
        </div>
        <button className="btn btn-danger-outline btn-sm" onClick={() => logout()}>Se déconnecter</button>
      </div>

      {me.referrer.status !== 'ACTIVE' && (
        <div className="error-banner">Votre compte apporteur est suspendu : aucune nouvelle commission n'est générée.</div>
      )}

      <section className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontWeight: 800 }}>Inviter un extra</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Partagez ce lien : chaque extra qui s'inscrit avec est rattaché à votre réseau, et vous percevez une part de la commission de
          Mon Extra à chaque mission clôturée.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ flex: 1, minWidth: 220, fontSize: 12.5, wordBreak: 'break-all', background: 'var(--bg)', padding: '10px 12px', borderRadius: 'var(--radius-md)' }}>{me.link}</code>
          <button className="btn btn-primary btn-sm" onClick={() => copy('link', me.link)}>
            {copied === 'link' ? <Check size={14} /> : <Copy size={14} />} {copied === 'link' ? 'Copié' : 'Copier le lien'}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          Votre code : <strong>{me.referrer.code}</strong>{' '}
          <button className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', width: 'auto' }} onClick={() => copy('code', me.referrer.code)}>
            {copied === 'code' ? 'Copié' : 'Copier'}
          </button>
        </div>
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        {kpis.map((k) => (
          <div key={k.label} className="card stat-card">
            <div className="stat-value">{k.value}</div>
            <div className="stat-label">{k.label}</div>
          </div>
        ))}
      </section>

      <PayoutSection dueCents={me.summary.due_cents} minPayoutCents={me.minPayoutCents} onChanged={loadAll} />

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Mes filleuls</div>
        {referrals.length === 0 && <div className="empty-state">Personne ne s'est encore inscrit avec votre lien.</div>}
        {referrals.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Extra</th><th>Inscrit le</th><th>Missions clôturées</th><th>Statut</th></tr></thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 700 }}>{r.firstName} {r.lastInitial}.</td>
                    <td>{new Date(r.joinedAt).toLocaleDateString('fr-FR')}</td>
                    <td>{r.closedMissions}</td>
                    <td><span className={`badge ${REFERRAL_LABELS[r.status].badge}`}>{REFERRAL_LABELS[r.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Mes commissions</div>
        {commissions.length === 0 && <div className="empty-state">Aucune commission pour l'instant : elles apparaissent à la clôture de la première mission d'un de vos filleuls.</div>}
        {commissions.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Extra</th><th>Taux</th><th>Commission</th><th>Statut</th><th>Payable le</th></tr></thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id}>
                    <td>{new Date(c.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>{c.extra_first_name}</td>
                    <td>{Number(c.share_pct)} %</td>
                    <td style={{ fontWeight: 700 }}>{formatEuros(c.amount_cents)}</td>
                    <td><span className={`badge ${COMMISSION_LABELS[c.status].badge}`}>{COMMISSION_LABELS[c.status].label}</span></td>
                    <td>{c.status === 'PAID' && c.paid_at ? `Versée le ${new Date(c.paid_at).toLocaleDateString('fr-FR')}` : new Date(c.eligible_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
